import IconExpand from '@apollo/icons/default/IconExpand.svg';
import IconExpandLinesDown from '@apollo/icons/default/IconExpandLinesDown.svg';
import IconExpandLinesUp from '@apollo/icons/default/IconExpandLinesUp.svg';
import classnames from 'classnames';
import { DocumentNode } from 'graphql';
import * as monacoEditor from 'monaco-editor';
import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { format } from 'src/lib/format';

import { useDerivedEditorValue } from '../../explorerPage/hooks/useDerivedEditorValue';

import {
  HIDDEN_SECTION_HEIGHT_IN_LINES,
  getFirstLineOfParentTypeAtLineNumber,
} from './diffEditorHelpers';

export type RangeExpandType = 'top' | 'bottom' | 'full';

export const HiddenSectionIndicators = ({
  editor,
  typeDefs,
  rangesToHide,
  handleExpandRange,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  typeDefs: DocumentNode;
  rangesToHide: monacoEditor.Range[];
  handleExpandRange: ({
    expandType,
    rangeIndex,
  }: {
    expandType: RangeExpandType;
    rangeIndex: number;
  }) => void;
}) => {
  return (
    <div>
      {rangesToHide.map((range, index) => {
        const startLineOfRange = range.startLineNumber;
        return (
          <HiddenSectionIndicator
            key={startLineOfRange}
            editor={editor}
            typeDefs={typeDefs}
            range={range}
            handleExpandRange={(expandType) =>
              handleExpandRange?.({ expandType, rangeIndex: index })
            }
          />
        );
      })}
    </div>
  );
};

const HiddenSectionIndicator = ({
  editor,
  typeDefs,
  range,
  handleExpandRange,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  typeDefs: DocumentNode;
  range: monacoEditor.Range;
  handleExpandRange: (expandType: RangeExpandType) => void;
}) => {
  // View zones do not work properly when attached to an editor before it is
  // mounted. This delayedEditor will not be populated until the first render
  // cycle has completed.
  const [delayedEditor, setDelayedEditor] =
    React.useState<monacoEditor.editor.IStandaloneCodeEditor>();

  const { modelTextValue, totalLineCount } = useDerivedEditorValue({
    editor,
    getValue: useCallback(
      (text: string) => ({
        modelTextValue: text,
        totalLineCount: editor.getModel()?.getLineCount(),
      }),
      [editor],
    ),
    withPosition: false,
  });

  const rangeStartsAtLine1 = range.startLineNumber === 1;
  const rangeEndsFile = range.endLineNumber === totalLineCount;

  const viewZoneClassname = classnames(
    'z-10',
    rangeStartsAtLine1 || rangeEndsFile ? '' : '-mt-10',
  );
  // Instead, the top actually pushes the editor down & the bottom is positioned under its line number.
  // However, The top & bottom view zones can't be absolutely positioned above, because they wouldn't be visible.
  // don't disappear when you scroll past the `afterLineNumber`.
  // ABOVE the `afterLineNumber` with -mt-10, instead of after, so that they
  // We abuse the positioning of the view zones & actually absolutely position them

  const viewZoneDomNode = React.useMemo(() => {
    const element = document.createElement('div');
    element.className = viewZoneClassname;
    return element;
  }, [viewZoneClassname]);
  const marginViewZoneDomNode = React.useMemo(() => {
    const element = document.createElement('div');
    element.className = viewZoneClassname;
    return element;
  }, [viewZoneClassname]);

  const currentViewZoneId = React.useRef<string>();

  const numberOfHiddenLines =
    range.endLineNumber -
    range.startLineNumber +
    // for the ranges that start at line 1, we don't overlay any lines with the hidden section indicator
    (rangeStartsAtLine1 ? 0 : HIDDEN_SECTION_HEIGHT_IN_LINES) +
    1; // + 1 to be inclusive of start + end;
  const numberOfHiddenLineTextContent = `${numberOfHiddenLines} ${format.pluralize(
    numberOfHiddenLines,
    'line',
  )}.`;
  const indicatorTextContent = React.useMemo(() => {
    const firstLineOfParentTypeAtEndLineNumber =
      getFirstLineOfParentTypeAtLineNumber(
        typeDefs,
        range.endLineNumber,
        editor.getModel(),
      );

    return ` ${numberOfHiddenLineTextContent} ${
      firstLineOfParentTypeAtEndLineNumber ?? ''
    }`;
  }, [editor, numberOfHiddenLineTextContent, range.endLineNumber, typeDefs]);
  React.useEffect(() => {
    // A view zone is a full horizontal rectangle that 'pushes' text down. The editor reserves space for view zones when rendering.
    // we position ours absolutely so that they don't push anything down and are just rendered over a line
    delayedEditor?.changeViewZones((changeAccessor) => {
      const newZone = {
        afterLineNumber: Math.max(
          0,
          // If the range is at the bottom of the file, we position BELOW instead of ABOVE
          // relative to this line number because otherwise the element won't be visible in scroll.
          range.startLineNumber -
            1 -
            (rangeEndsFile ? HIDDEN_SECTION_HEIGHT_IN_LINES : 0) -
            // the extra -1 is here because monaco doesn't show the indicator if its on the line that is directly above a hidden line, so we shift it up one and add margin
            1,
        ),
        // If this range starts at line 1, we need to push down the editor for the view zone to be seen
        heightInLines: rangeStartsAtLine1 ? HIDDEN_SECTION_HEIGHT_IN_LINES : 0,
        domNode: viewZoneDomNode,
        marginDomNode: marginViewZoneDomNode,
      };
      currentViewZoneId.current = changeAccessor.addZone(newZone);
    });

    return () => {
      const lastZoneId = currentViewZoneId.current;
      if (!lastZoneId) return;
      delayedEditor?.changeViewZones((changeAccessor) => {
        changeAccessor.removeZone(lastZoneId);
      });
      currentViewZoneId.current = undefined;
    };
    // when models change, view zones are lost so it needs to be in the dependency array
  }, [
    delayedEditor,
    viewZoneDomNode,
    modelTextValue,
    range,
    rangeStartsAtLine1,
    marginViewZoneDomNode,
    editor,
    rangeEndsFile,
  ]);

  React.useEffect(() => {
    setDelayedEditor(editor);
  }, [editor]);

  return (
    <>
      {createPortal(
        <div className="mt-5 flex h-10 w-full items-center bg-info">
          <div className="ml-4 truncate font-mono text-xs text-secondary">
            {indicatorTextContent}
          </div>
        </div>,
        viewZoneDomNode,
      )}
      {
        // If this indicator has > 20 lines hidden, we want to allow you to expand 10 at a time,
        // otherwise you can only expand them all.
      }
      {createPortal(
        <div className="mt-5 flex h-10 w-full cursor-pointer flex-col items-center bg-info text-secondary">
          {numberOfHiddenLines > 20 ? (
            <>
              <Tooltip label="Expand top 10 lines">
                <div
                  className={classnames(
                    'group flex h-1/2 w-full items-center justify-center hover:bg-blue ',
                  )}
                  onClick={() => handleExpandRange('top')}
                >
                  <IconExpandLinesUp className="group-hover:text-inverted" />
                </div>
              </Tooltip>
              <Tooltip label="Expand bottom 10 lines">
                <div
                  className={classnames(
                    'group flex h-1/2 w-full items-center justify-center hover:bg-blue',
                  )}
                  onClick={() => handleExpandRange('bottom')}
                >
                  <IconExpandLinesDown className=" group-hover:text-inverted" />
                </div>
              </Tooltip>
            </>
          ) : (
            <>
              <Tooltip label={`Expand ${numberOfHiddenLineTextContent}`}>
                <div
                  className="group flex size-full items-center justify-center hover:bg-blue"
                  onClick={() => handleExpandRange('full')}
                >
                  {range.endLineNumber === totalLineCount && (
                    <IconExpandLinesUp className="group-hover:text-inverted" />
                  )}
                  {range.startLineNumber === 1 && (
                    <IconExpandLinesDown className="group-hover:text-inverted" />
                  )}
                  {range.endLineNumber !== totalLineCount &&
                    range.startLineNumber !== 1 && (
                      <IconExpand className="group-hover:text-inverted" />
                    )}
                </div>
              </Tooltip>
            </>
          )}
        </div>,
        marginViewZoneDomNode,
      )}
    </>
  );
};
